import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Text,
  Captcha,
  Form,
  ResponsiveContext,
  Loader,
} from '@usap/uicomponents';
import queryString from 'query-string';
import { trackOrderSchema } from '../common/ValidationSchema';
import validate from '../../helpers/validation';
import FormField from '../../components/Form/FormField';
import useViewPort from '../../../../carparts/src/hooks/useViewPort'
import {
  FieldHeader,
  GrommetContainer,
  SubHeading,
  ShippingCard,
  SubmitButton,
  TitleText,
  TrackContainer,
  TrackCard,
  LinkText,
  TipContainer,
  TipText,
  ContactUsWrapper,
  MobileDisplayOnly,
  DesktopDisplayOnly,
  ChatCardContainer,
  MainContainer,
  LeftContainer,
  UptickWrapper,
} from './style/TrackOrderWmo.css';
import { GTM } from '@usap/carparts/src/shared/helpers';
import EnablePushNotificationModal from '@usap/carparts/src/shared/components/common/EnablePushNotificationModal';
import useIsInvisibleCaptcha from '@usap/carparts/src/shared/hooks/useIsInvisibleCaptcha';
import useRecaptchaLoader from '@usap/carparts/src/shared/hooks/useRecaptchaLoader';
import ChatAssistantCard from './ChatAssistantCard';
import AIChatFeedbackModal from './AIChatFeedbackModal';
import useIsChatEnabled from '@usap/carparts/src/shared/hooks/useIsChatEnabled';
import SupportBarTrackOrder from './SupportBarTrackOrder';
import ShippingNotesTrackOrder from './RMA/Forms/ShippingNotesTrackOrder';

export default function TrackOrderWmo({
  getMyOrder,
  error,
  siteKey,
  isDesktop,
  pushNotificationData,
  loading,
  recommendationsComponent,
  chatAssistantData,
}) {
  const {
    isRecaptchaLoaded,
    error: captchaError,
    waitForRecaptcha,
  } = useRecaptchaLoader();
  const [isLoading, setIsLoading] = useState(loading);
  const captchaErrorRef = useRef(null);
  const recaptchaRef = useRef(null);
  const { isMobile } = useViewPort()

  const isChatEnabled = useIsChatEnabled();

  useEffect(() => {
    setIsLoading(loading);
  }, [loading]);

  const [errors, setError] = useState({});
  const [formData, setFormData] = useState({
    email: '',
    order: '',
  });
  const [isFormValid, setIsFormValid] = useState(false);
  const isFormFieldClicked = useRef(null);
  const { customFormStart } = GTM;
  const isInvisibleCaptcha = useIsInvisibleCaptcha()

  const onInputChangeHandler = (event) => {
    const { name, value } = event.target;
    setError({ ...errors, [name]: '' });
    setFormData({ ...formData, [name]: value });
    if (!isFormFieldClicked.current) {
      isFormFieldClicked.current = true;
      customFormStart('Track order', 'submitForm');
    }
  };

  const resetClick = (name, event) => {
    // eslint-disable-next-line no-param-reassign
    event.target.name = name;
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
    onInputChangeHandler(event);
    setFormData({ ...formData, [name]: '' });
  };

  const trackMyOrder = ({ email, order, captcha }) => {
    getMyOrder(email, order, captcha);
  };

  const onFormSubmit = useCallback(
    (captcha) => {
      if (captcha) {
        formData.captcha = captcha;
      }
      const evalidationErrors = validate(
        formData,
        trackOrderSchema(isInvisibleCaptcha)
      );

      if (evalidationErrors) {
        return setError(evalidationErrors);
      }
      return trackMyOrder(formData);
    },
    [formData, trackMyOrder]
  );

  useEffect(() => {
    if (captchaError) captchaErrorRef.current = captchaError;
  }, [captchaError]);

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    if (!isInvisibleCaptcha) return onFormSubmit();
    setIsLoading(true);
    try {
      // Wait for reCAPTCHA to load, and if it fails, return with the captcha error message without api call      if (!isRecaptchaLoaded) await waitForRecaptcha();
      if (!isRecaptchaLoaded) await waitForRecaptcha();
      if (captchaErrorRef.current) {
        setLoading(false);
        return;
      }
      // Trigger reCAPTCHA execution from the child component
      const token = await recaptchaRef.current.executeRecaptcha();

      if (!token) {
        console.error('reCAPTCHA validation failed.');
        return;
      }
      onFormSubmit(token);
    } catch (error) {
      console.error('Error during reCAPTCHA execution:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const setCaptchState = useCallback(
    (captchaValue) => {
      setError({ ...errors, captcha: false });
      setFormData({ ...formData, captcha: captchaValue });
    },
    [errors, formData]
  );

  useEffect(() => {
    const parsed = queryString.parse(window.location.search);
    const { emailId = '', orderId = '' } = parsed;

    if (emailId && orderId) {
      setFormData({
        email: emailId,
        order: orderId,
      });
    }
  }, []);

  useEffect(() => {
    const hasError = validate(formData, trackOrderSchema(formData));
    !hasError ? setIsFormValid(true) : setIsFormValid(false);
  }, [formData]);

  return (
    <>
      <GrommetContainer>
        {isLoading && <Loader />}
        <TrackContainer isDesktop={isDesktop}>
          <ResponsiveContext.Consumer>
            {(size) => {
              return (
                <>
                  {isChatEnabled && (
                    <ChatCardContainer showOnMobile={true}>
                      <ChatAssistantCard
                        chatAssistantData={chatAssistantData}
                      />
                    </ChatCardContainer>
                  )}
                  <LeftContainer>
                    <TrackCard isDesktop={isDesktop}>
                      <TitleText id="Track Order">
                        Track Order
                      </TitleText>

                      <SubHeading>
                        Have an account?{' '}
                        <LinkText
                          href={`/myaccount/login?referer=${window.location.pathname}`}
                        >
                          Sign in
                        </LinkText>{' '}
                        to view your full order history.
                      </SubHeading>
                      <FieldHeader>
                        <span style={{ color: 'red' }}>*</span>
                        Fields are required
                      </FieldHeader>

                      <Form id="submitForm" onSubmit={handleFormSubmit}>
                        <Box margin={{ bottom: '16px' }}>
                          <FormField
                            label="Email Address"
                            placeholder=" "
                            autoComplete="email-address"
                            id="email"
                            name="email"
                            aria-label="email address"
                            type="email"
                            required
                            error={errors.email}
                            onChange={onInputChangeHandler}
                            defaultValue={formData.email}
                          />
                          {errors.email && (
                            <Box width="100%">
                              <Text color="#f44635" size="xsmall">
                                Please enter a valid email
                              </Text>
                            </Box>
                          )}
                        </Box>

                        <Box>
                          <FormField
                            label="Order Number"
                            placeholder=" "
                            autoComplete="order-number"
                            id="order"
                            name="order"
                            aria-label="order number"
                            error={errors.order}
                            onChange={onInputChangeHandler}
                            defaultValue={formData.order}
                          />
                          {errors.order && (
                            <Box width="100%">
                              <Text color="#f44635" size="xsmall">
                                Please provide a valid Order ID
                              </Text>
                            </Box>
                          )}
                        </Box>

                        <TipContainer>
                          <TipText>
                            Tip: Check your Order Confirmation email for your
                            Order Number.
                          </TipText>
                        </TipContainer>

                        <Box pad={{ vertical: 'medium' }} align="flex-start">
                          <Captcha
                            id="guest-form"
                            name="guest-form-captcha"
                            setCaptcha={setCaptchState}
                            error={errors.captcha}
                            siteKey={siteKey}
                            ref={recaptchaRef}
                          />
                        </Box>

                        <Box>
                          <SubmitButton
                            id="trackOrder"
                            form="submitForm"
                            primary
                            type="submit"
                            custom
                            hideBorder
                            size="medium"
                            label="Track Order"
                            hoverBackground="hoverDarkSecondary"
                            color="dark-2"
                            disabled={!isFormValid}
                          />
                        </Box>

                        {[error, captchaError].map(
                          (err, index) =>
                            err && (
                              <Box
                                key={index}
                                align="center"
                                height="14px"
                                pad={{ top: 'xsmall' }}
                              >
                                <Text color="red" size="xsmall">
                                  {err}
                                </Text>
                              </Box>
                            )
                        )}
                      </Form>
                    </TrackCard>
                    <MobileDisplayOnly>
                      {recommendationsComponent}
                   </MobileDisplayOnly>
                    <ContactUsWrapper showOnMobile={false}>
                      <SupportBarTrackOrder isChatEnabled={isChatEnabled} />
                    </ContactUsWrapper>
                    { !isMobile && <UptickWrapper id="uptick-offers" data-uptick-target="content" class="uptick-target section"data-test-id="uptick-container" style={{marginTop: "16px", marginBottom: "0px"}}/> }
                  </LeftContainer>
                  {isChatEnabled ? (
                    <MainContainer>
                      <ChatCardContainer showOnMobile={false}>
                        <ChatAssistantCard
                          chatAssistantData={chatAssistantData}
                        />
                      </ChatCardContainer>
                      <ShippingCard isDesktop={isDesktop}>
                        <ShippingNotesTrackOrder />
                      </ShippingCard>
                    </MainContainer>
                  ) : (
                    <ShippingCard isDesktop={isDesktop}>
                      <ShippingNotesTrackOrder />
                    </ShippingCard>
                  )}
                    <ContactUsWrapper showOnMobile={true}>
                      <SupportBarTrackOrder isChatEnabled={isChatEnabled} />
                    </ContactUsWrapper>
                    { isMobile && <UptickWrapper id="uptick-offers" data-uptick-target="content" class="uptick-target section"data-test-id="uptick-container" isMobile={isMobile}/> }
                </>
              );
            }}
          </ResponsiveContext.Consumer>
        </TrackContainer>
        <EnablePushNotificationModal content={pushNotificationData} />
      </GrommetContainer>
      <DesktopDisplayOnly>{recommendationsComponent}</DesktopDisplayOnly>
      <AIChatFeedbackModal content={chatAssistantData?.feedbackModalContent} />
    </>
  );
}
